import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Save, Download, RotateCcw, AlertTriangle, Filter, SortAsc, SortDesc, User, Eye, EyeOff } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

interface SpreadsheetViewerProps {
  data: any[][];
  columnDefinitions: any[];
  onDataChange: (newData: any[][]) => void;
  onSave: () => void;
  onExport: () => void;
  onRevert: () => void;
  hasUnsavedChanges: boolean;
  isLoading?: boolean;
}

interface StudentProfile {
  user_id: number;
  profile_image: string;
  bio: string;
}

const SpreadsheetViewer: React.FC<SpreadsheetViewerProps> = ({
  data,
  columnDefinitions,
  onDataChange,
  onSave,
  onExport,
  onRevert,
  hasUnsavedChanges,
  isLoading
}) => {
  const [sortConfig, setSortConfig] = useState<{key: number;direction: 'asc' | 'desc';} | null>(null);
  const [filters, setFilters] = useState<{[key: number]: string;}>({});
  const [editingCell, setEditingCell] = useState<{row: number;col: number;} | null>(null);
  const [editValue, setEditValue] = useState('');
  const [frozenColumns, setFrozenColumns] = useState(2);
  const [studentProfiles, setStudentProfiles] = useState<{[key: string]: StudentProfile;}>({});
  const [showFormulaCells, setShowFormulaCells] = useState(true);
  const { toast } = useToast();

  // Load student profiles
  useEffect(() => {
    const loadStudentProfiles = async () => {
      try {
        const { data: profiles, error } = await window.ezsite.apis.tablePage(16056, {
          PageNo: 1,
          PageSize: 100,
          OrderByField: 'id',
          IsAsc: true
        });

        if (error) throw error;

        const profileMap: {[key: string]: StudentProfile;} = {};
        profiles.List.forEach((profile: any) => {
          profileMap[profile.user_id] = profile;
        });
        setStudentProfiles(profileMap);
      } catch (error) {
        console.error('Error loading student profiles:', error);
      }
    };

    loadStudentProfiles();
  }, []);

  // Enhanced column grouping based on headers
  const groupedColumns = useMemo(() => {
    if (!data || data.length === 0) return [];

    const headers = data[0] || [];
    const groups: Array<{title: string;columns: number[];color: string;}> = [];

    const attendancePattern = /attendance|present|absent/i;
    const assignmentPattern = /assignment|hw|homework|quiz/i;
    const examPattern = /exam|test|midterm|final/i;
    const gradePattern = /grade|total|average|final/i;

    headers.forEach((header, index) => {
      const headerText = header.value || header.toString();

      if (index < 2) {
        if (groups.length === 0 || groups[groups.length - 1].title !== 'Student Info') {
          groups.push({ title: 'Student Info', columns: [index], color: 'bg-blue-50 border-blue-200' });
        } else {
          groups[groups.length - 1].columns.push(index);
        }
      } else if (attendancePattern.test(headerText)) {
        if (groups.length === 0 || groups[groups.length - 1].title !== 'Attendance') {
          groups.push({ title: 'Attendance', columns: [index], color: 'bg-purple-50 border-purple-200' });
        } else {
          groups[groups.length - 1].columns.push(index);
        }
      } else if (assignmentPattern.test(headerText)) {
        if (groups.length === 0 || groups[groups.length - 1].title !== 'Assignments') {
          groups.push({ title: 'Assignments', columns: [index], color: 'bg-green-50 border-green-200' });
        } else {
          groups[groups.length - 1].columns.push(index);
        }
      } else if (examPattern.test(headerText)) {
        if (groups.length === 0 || groups[groups.length - 1].title !== 'Exams') {
          groups.push({ title: 'Exams', columns: [index], color: 'bg-orange-50 border-orange-200' });
        } else {
          groups[groups.length - 1].columns.push(index);
        }
      } else if (gradePattern.test(headerText)) {
        if (groups.length === 0 || groups[groups.length - 1].title !== 'Final Grades') {
          groups.push({ title: 'Final Grades', columns: [index], color: 'bg-yellow-50 border-yellow-200' });
        } else {
          groups[groups.length - 1].columns.push(index);
        }
      } else {
        if (groups.length === 0 || groups[groups.length - 1].title !== 'Other') {
          groups.push({ title: 'Other', columns: [index], color: 'bg-gray-50 border-gray-200' });
        } else {
          groups[groups.length - 1].columns.push(index);
        }
      }
    });

    return groups;
  }, [data]);

  // Processed data with sorting and filtering
  const processedData = useMemo(() => {
    if (!data || data.length <= 1) return data;

    let processed = [...data];
    const dataRows = processed.slice(1);

    const filteredRows = dataRows.filter((row) => {
      return Object.entries(filters).every(([colIndex, filterValue]) => {
        if (!filterValue) return true;
        const cellValue = row[parseInt(colIndex)]?.value?.toString().toLowerCase() || '';
        return cellValue.includes(filterValue.toLowerCase());
      });
    });

    if (sortConfig) {
      filteredRows.sort((a, b) => {
        const aVal = a[sortConfig.key]?.value || '';
        const bVal = b[sortConfig.key]?.value || '';

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        const aStr = aVal.toString();
        const bStr = bVal.toString();
        const comparison = aStr.localeCompare(bStr);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return [processed[0], ...filteredRows];
  }, [data, sortConfig, filters]);

  const handleSort = (colIndex: number) => {
    setSortConfig((prev) => {
      if (prev?.key === colIndex) {
        return prev.direction === 'asc' ?
        { key: colIndex, direction: 'desc' } :
        null;
      }
      return { key: colIndex, direction: 'asc' };
    });
  };

  const handleFilter = (colIndex: number, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [colIndex]: value
    }));
  };

  const handleCellClick = (rowIndex: number, colIndex: number) => {
    if (rowIndex === 0) return;

    const cell = processedData[rowIndex]?.[colIndex];
    if (cell?.isFormula && showFormulaCells) return;

    setEditingCell({ row: rowIndex, col: colIndex });
    setEditValue(cell?.value?.toString() || '');
  };

  const handleCellSave = () => {
    if (!editingCell) return;

    const newData = [...data];
    const { row, col } = editingCell;

    if (newData[row] && newData[row][col]) {
      newData[row][col] = {
        ...newData[row][col],
        value: editValue
      };
    }

    onDataChange(newData);
    setEditingCell(null);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCellSave();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  const renderCell = (cell: any, rowIndex: number, colIndex: number) => {
    const isEditing = editingCell?.row === rowIndex && editingCell?.col === colIndex;
    const isFormula = cell?.isFormula;
    const isHeader = rowIndex === 0;
    const value = cell?.value || '';

    if (colIndex === 0 && !isHeader && value) {
      const studentId = processedData[rowIndex]?.[1]?.value;
      const profile = studentProfiles[studentId];

      return (
        <div className="flex items-center gap-2 p-2" data-id="kwlpi95w0" data-path="src/components/grades/SpreadsheetViewer.tsx">
          <Avatar className="h-8 w-8" data-id="g9mllue5d" data-path="src/components/grades/SpreadsheetViewer.tsx">
            <AvatarImage src={profile?.profile_image} data-id="pzhzx6hlp" data-path="src/components/grades/SpreadsheetViewer.tsx" />
            <AvatarFallback data-id="ne25jqbg5" data-path="src/components/grades/SpreadsheetViewer.tsx">
              <User className="h-4 w-4" data-id="n1xkvu2gc" data-path="src/components/grades/SpreadsheetViewer.tsx" />
            </AvatarFallback>
          </Avatar>
          <span className="font-medium" data-id="gxz0b8sbu" data-path="src/components/grades/SpreadsheetViewer.tsx">{value}</span>
        </div>);

    }

    if (isEditing) {
      return (
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleCellSave}
          className="border-blue-500 focus:border-blue-600"
          autoFocus data-id="p7cbkiche" data-path="src/components/grades/SpreadsheetViewer.tsx" />);


    }

    return (
      <div
        className={`p-2 cursor-pointer hover:bg-gray-50 ${
        isFormula ? 'bg-green-50 italic font-medium' : ''} ${
        isHeader ? 'font-bold text-center' : ''}`}
        onClick={() => handleCellClick(rowIndex, colIndex)} data-id="g16007bsd" data-path="src/components/grades/SpreadsheetViewer.tsx">

        {isFormula &&
        <Badge variant="secondary" className="text-xs mr-1" data-id="n7u98zz3b" data-path="src/components/grades/SpreadsheetViewer.tsx">
            =
          </Badge>
        }
        {typeof value === 'number' && !isHeader ? value.toFixed(2) : value}
      </div>);

  };

  const renderHeaderCell = (header: any, colIndex: number) => {
    const headerText = header?.value || header?.toString() || `Column ${colIndex + 1}`;
    const isSorted = sortConfig?.key === colIndex;

    return (
      <div className="flex items-center justify-between p-2 bg-gray-100 border-b-2 border-gray-300" data-id="laqs7zgiw" data-path="src/components/grades/SpreadsheetViewer.tsx">
        <div className="flex items-center gap-2" data-id="9th4kui5r" data-path="src/components/grades/SpreadsheetViewer.tsx">
          <span className="font-bold text-sm" data-id="098za9uxu" data-path="src/components/grades/SpreadsheetViewer.tsx">{headerText}</span>
          {isSorted && (
          sortConfig.direction === 'asc' ?
          <SortAsc className="h-3 w-3 text-blue-600" data-id="s8wl5dglh" data-path="src/components/grades/SpreadsheetViewer.tsx" /> :
          <SortDesc className="h-3 w-3 text-blue-600" data-id="xcclm3lj2" data-path="src/components/grades/SpreadsheetViewer.tsx" />)
          }
        </div>
        
        <div className="flex items-center gap-1" data-id="1c63ufs5d" data-path="src/components/grades/SpreadsheetViewer.tsx">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleSort(colIndex)}
            className="h-6 w-6 p-0" data-id="ps9ry2goo" data-path="src/components/grades/SpreadsheetViewer.tsx">

            <SortAsc className="h-3 w-3" data-id="puj5zqk3k" data-path="src/components/grades/SpreadsheetViewer.tsx" />
          </Button>
          
          <Popover data-id="tt0ktmvur" data-path="src/components/grades/SpreadsheetViewer.tsx">
            <PopoverTrigger asChild data-id="t19o8clu4" data-path="src/components/grades/SpreadsheetViewer.tsx">
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" data-id="lxul1okyn" data-path="src/components/grades/SpreadsheetViewer.tsx">
                <Filter className="h-3 w-3" data-id="brug5cv28" data-path="src/components/grades/SpreadsheetViewer.tsx" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64" data-id="833rwpkte" data-path="src/components/grades/SpreadsheetViewer.tsx">
              <div className="space-y-2" data-id="ewh2kyy7f" data-path="src/components/grades/SpreadsheetViewer.tsx">
                <Label data-id="w4mft5g95" data-path="src/components/grades/SpreadsheetViewer.tsx">Filter by value</Label>
                <Input
                  placeholder="Enter filter value..."
                  value={filters[colIndex] || ''}
                  onChange={(e) => handleFilter(colIndex, e.target.value)} data-id="jz44r487p" data-path="src/components/grades/SpreadsheetViewer.tsx" />

              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>);

  };

  if (!data || data.length === 0) {
    return (
      <Card data-id="3jvt7ynn5" data-path="src/components/grades/SpreadsheetViewer.tsx">
        <CardContent className="text-center py-12" data-id="ca29ixojq" data-path="src/components/grades/SpreadsheetViewer.tsx">
          <p className="text-gray-600" data-id="8r044x7n1" data-path="src/components/grades/SpreadsheetViewer.tsx">No data to display</p>
        </CardContent>
      </Card>);

  }

  return (
    <Card className="w-full" data-id="64x1z31bx" data-path="src/components/grades/SpreadsheetViewer.tsx">
      <CardHeader data-id="1zbxw09zc" data-path="src/components/grades/SpreadsheetViewer.tsx">
        <div className="flex items-center justify-between" data-id="2m6h3wm0f" data-path="src/components/grades/SpreadsheetViewer.tsx">
          <div data-id="kzf5bsjtt" data-path="src/components/grades/SpreadsheetViewer.tsx">
            <CardTitle className="flex items-center gap-2" data-id="uh8349ue6" data-path="src/components/grades/SpreadsheetViewer.tsx">
              Professional Grade Sheet Editor
              {hasUnsavedChanges &&
              <Badge variant="outline" className="text-orange-600" data-id="v2v4sf0tl" data-path="src/components/grades/SpreadsheetViewer.tsx">
                  <AlertTriangle className="h-3 w-3 mr-1" data-id="5yrlkfy3k" data-path="src/components/grades/SpreadsheetViewer.tsx" />
                  Unsaved Changes
                </Badge>
              }
            </CardTitle>
            <CardDescription data-id="ktimva22k" data-path="src/components/grades/SpreadsheetViewer.tsx">
              Professional Excel-like interface with advanced features
            </CardDescription>
          </div>
          
          <div className="flex gap-2" data-id="ysm5futdl" data-path="src/components/grades/SpreadsheetViewer.tsx">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFormulaCells(!showFormulaCells)} data-id="2l5i1vxj3" data-path="src/components/grades/SpreadsheetViewer.tsx">

              {showFormulaCells ? <Eye className="h-4 w-4 mr-1" data-id="8n2ho1gec" data-path="src/components/grades/SpreadsheetViewer.tsx" /> : <EyeOff className="h-4 w-4 mr-1" data-id="dcipofcq9" data-path="src/components/grades/SpreadsheetViewer.tsx" />}
              Formulas
            </Button>
            
            <Select value={frozenColumns.toString()} onValueChange={(v) => setFrozenColumns(parseInt(v))} data-id="jc271a5yp" data-path="src/components/grades/SpreadsheetViewer.tsx">
              <SelectTrigger className="w-32" data-id="oxj080axb" data-path="src/components/grades/SpreadsheetViewer.tsx">
                <SelectValue data-id="ebzzivxdo" data-path="src/components/grades/SpreadsheetViewer.tsx" />
              </SelectTrigger>
              <SelectContent data-id="uwap365i1" data-path="src/components/grades/SpreadsheetViewer.tsx">
                <SelectItem value="0" data-id="wwjuczcnk" data-path="src/components/grades/SpreadsheetViewer.tsx">No Freeze</SelectItem>
                <SelectItem value="1" data-id="z1qih3kic" data-path="src/components/grades/SpreadsheetViewer.tsx">Freeze 1</SelectItem>
                <SelectItem value="2" data-id="duhkcd8jj" data-path="src/components/grades/SpreadsheetViewer.tsx">Freeze 2</SelectItem>
                <SelectItem value="3" data-id="ag1zur26h" data-path="src/components/grades/SpreadsheetViewer.tsx">Freeze 3</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="sm"
              onClick={onRevert}
              disabled={!hasUnsavedChanges || isLoading} data-id="9rysmzmmo" data-path="src/components/grades/SpreadsheetViewer.tsx">

              <RotateCcw className="h-4 w-4 mr-2" data-id="lmt307ht8" data-path="src/components/grades/SpreadsheetViewer.tsx" />
              Revert
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={onSave}
              disabled={!hasUnsavedChanges || isLoading} data-id="0u35jsqao" data-path="src/components/grades/SpreadsheetViewer.tsx">

              <Save className="h-4 w-4 mr-2" data-id="wvif9j9fm" data-path="src/components/grades/SpreadsheetViewer.tsx" />
              Save Changes
            </Button>
            
            <Button
              size="sm"
              onClick={onExport}
              disabled={isLoading} data-id="lu64uztfw" data-path="src/components/grades/SpreadsheetViewer.tsx">

              <Download className="h-4 w-4 mr-2" data-id="sgkizk8u4" data-path="src/components/grades/SpreadsheetViewer.tsx" />
              Export Excel
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent data-id="w4qtdzjh2" data-path="src/components/grades/SpreadsheetViewer.tsx">
        {hasUnsavedChanges &&
        <Alert className="mb-4" data-id="iyl8aja8f" data-path="src/components/grades/SpreadsheetViewer.tsx">
            <AlertTriangle className="h-4 w-4" data-id="5asujzoj3" data-path="src/components/grades/SpreadsheetViewer.tsx" />
            <AlertDescription data-id="24angibzl" data-path="src/components/grades/SpreadsheetViewer.tsx">
              You have unsaved changes. Remember to save your work before closing.
            </AlertDescription>
          </Alert>
        }

        {/* Column Group Headers */}
        <div className="mb-4 overflow-x-auto" data-id="ecqjl824t" data-path="src/components/grades/SpreadsheetViewer.tsx">
          <div className="grid grid-flow-col auto-cols-max gap-0 border-b" data-id="h96p0qnoc" data-path="src/components/grades/SpreadsheetViewer.tsx">
            {groupedColumns.map((group, groupIndex) =>
            <div
              key={groupIndex}
              className={`${group.color} border-r border-l p-2 text-center font-bold text-sm`}
              style={{ gridColumn: `span ${group.columns.length}` }} data-id="ivnwlset0" data-path="src/components/grades/SpreadsheetViewer.tsx">

                {group.title}
              </div>
            )}
          </div>
        </div>

        {/* Main Spreadsheet */}
        <div className="border rounded-lg overflow-hidden" data-id="rppu8jyho" data-path="src/components/grades/SpreadsheetViewer.tsx">
          <div className="overflow-auto max-h-[600px] relative" data-id="1hvhlvw5i" data-path="src/components/grades/SpreadsheetViewer.tsx">
            <table className="w-full border-collapse" data-id="ybiu1nlu1" data-path="src/components/grades/SpreadsheetViewer.tsx">
              <thead className="sticky top-0 z-10" data-id="2lgkzyvwb" data-path="src/components/grades/SpreadsheetViewer.tsx">
                <tr data-id="f6sp6dmq9" data-path="src/components/grades/SpreadsheetViewer.tsx">
                  {(processedData[0] || []).map((header, colIndex) =>
                  <th
                    key={colIndex}
                    className={`border border-gray-300 bg-gray-100 min-w-[120px] ${
                    colIndex < frozenColumns ? 'sticky left-0 z-20' : ''}`
                    }
                    style={colIndex < frozenColumns ? { left: `${colIndex * 120}px` } : {}} data-id="ve205rztw" data-path="src/components/grades/SpreadsheetViewer.tsx">

                      {renderHeaderCell(header, colIndex)}
                    </th>
                  )}
                </tr>
              </thead>
              
              <tbody data-id="xs0bxe3zl" data-path="src/components/grades/SpreadsheetViewer.tsx">
                {processedData.slice(1).map((row, rowIndex) =>
                <tr key={rowIndex + 1} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="uoy3xag0h" data-path="src/components/grades/SpreadsheetViewer.tsx">
                    {row.map((cell, colIndex) =>
                  <td
                    key={colIndex}
                    className={`border border-gray-300 min-w-[120px] ${
                    colIndex < frozenColumns ? 'sticky left-0 z-10 bg-inherit' : ''}`
                    }
                    style={colIndex < frozenColumns ? { left: `${colIndex * 120}px` } : {}} data-id="suuew0zu3" data-path="src/components/grades/SpreadsheetViewer.tsx">

                        {renderCell(cell, rowIndex + 1, colIndex)}
                      </td>
                  )}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg" data-id="e5mp156pq" data-path="src/components/grades/SpreadsheetViewer.tsx">
          <p className="font-semibold mb-2" data-id="r9sst19yg" data-path="src/components/grades/SpreadsheetViewer.tsx">Legend:</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-id="hxtuliqub" data-path="src/components/grades/SpreadsheetViewer.tsx">
            <div className="flex items-center gap-2" data-id="kmije1qpn" data-path="src/components/grades/SpreadsheetViewer.tsx">
              <div className="w-4 h-4 bg-white border border-gray-300" data-id="ighes41a7" data-path="src/components/grades/SpreadsheetViewer.tsx"></div>
              <span data-id="98cl2fkd2" data-path="src/components/grades/SpreadsheetViewer.tsx">Editable cells</span>
            </div>
            <div className="flex items-center gap-2" data-id="ivykrrhtq" data-path="src/components/grades/SpreadsheetViewer.tsx">
              <div className="w-4 h-4 bg-green-50 border border-gray-300" data-id="6jm7l96ug" data-path="src/components/grades/SpreadsheetViewer.tsx"></div>
              <span data-id="8dz84izi1" data-path="src/components/grades/SpreadsheetViewer.tsx">Formula cells</span>
            </div>
            <div className="flex items-center gap-2" data-id="44bblob7y" data-path="src/components/grades/SpreadsheetViewer.tsx">
              <div className="w-4 h-4 bg-gray-100 border border-gray-300" data-id="iiv3awb9i" data-path="src/components/grades/SpreadsheetViewer.tsx"></div>
              <span data-id="13nq3945o" data-path="src/components/grades/SpreadsheetViewer.tsx">Header cells</span>
            </div>
            <div className="flex items-center gap-2" data-id="r5ly95gzn" data-path="src/components/grades/SpreadsheetViewer.tsx">
              <Avatar className="h-4 w-4" data-id="gc001at40" data-path="src/components/grades/SpreadsheetViewer.tsx">
                <AvatarFallback data-id="u178wc1jg" data-path="src/components/grades/SpreadsheetViewer.tsx"><User className="h-2 w-2" data-id="3zvu2g9v2" data-path="src/components/grades/SpreadsheetViewer.tsx" /></AvatarFallback>
              </Avatar>
              <span data-id="c6vbfl9zz" data-path="src/components/grades/SpreadsheetViewer.tsx">Student profiles</span>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-id="wj2v4w24b" data-path="src/components/grades/SpreadsheetViewer.tsx">
          <div className="bg-blue-50 p-3 rounded" data-id="sfb1w645q" data-path="src/components/grades/SpreadsheetViewer.tsx">
            <div className="font-semibold text-blue-800" data-id="q5neyeaws" data-path="src/components/grades/SpreadsheetViewer.tsx">Total Students</div>
            <div className="text-xl font-bold text-blue-600" data-id="ydhw6rlru" data-path="src/components/grades/SpreadsheetViewer.tsx">{processedData.length - 1}</div>
          </div>
          <div className="bg-green-50 p-3 rounded" data-id="uxpwh01qf" data-path="src/components/grades/SpreadsheetViewer.tsx">
            <div className="font-semibold text-green-800" data-id="pagar8s1a" data-path="src/components/grades/SpreadsheetViewer.tsx">Total Columns</div>
            <div className="text-xl font-bold text-green-600" data-id="jh9k8j35u" data-path="src/components/grades/SpreadsheetViewer.tsx">{processedData[0]?.length || 0}</div>
          </div>
          <div className="bg-orange-50 p-3 rounded" data-id="7meybzafm" data-path="src/components/grades/SpreadsheetViewer.tsx">
            <div className="font-semibold text-orange-800" data-id="oj92otcp7" data-path="src/components/grades/SpreadsheetViewer.tsx">Active Filters</div>
            <div className="text-xl font-bold text-orange-600" data-id="ypvsh4zau" data-path="src/components/grades/SpreadsheetViewer.tsx">{Object.keys(filters).filter((k) => filters[parseInt(k)]).length}</div>
          </div>
          <div className="bg-purple-50 p-3 rounded" data-id="r5497922i" data-path="src/components/grades/SpreadsheetViewer.tsx">
            <div className="font-semibold text-purple-800" data-id="ayr8bleax" data-path="src/components/grades/SpreadsheetViewer.tsx">Frozen Columns</div>
            <div className="text-xl font-bold text-purple-600" data-id="vnghgp6ct" data-path="src/components/grades/SpreadsheetViewer.tsx">{frozenColumns}</div>
          </div>
        </div>
      </CardContent>
    </Card>);

};

export default SpreadsheetViewer;